import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  IconButton,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import NotificationsOffOutlinedIcon from "@mui/icons-material/NotificationsOffOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { RootState } from "../../redux/store";
import {
  clearBurst,
  toggleOrderNotificationMute,
} from "../../redux/slices/OrderNotificationState";

const SOUND_COOLDOWN_MS = 15000;

export function NewOrderAlert() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { burstCount, muted, unseenOrderIds, unseenSessionIds } = useSelector(
    (state: RootState) => state.OrderNotification
  );
  const [open, setOpen] = useState(false);
  const [toastCount, setToastCount] = useState(0);
  const lastSoundAtRef = useRef(0);
  const isOnOrdersPage = location.pathname === "/app/order";

  useEffect(() => {
    if (!burstCount) return;

    setToastCount(burstCount);

    if (!isOnOrdersPage) {
      setOpen(true);
    }

    const now = Date.now();
    if (!muted && now - lastSoundAtRef.current > SOUND_COOLDOWN_MS) {
      const audio = new Audio("/sounds/order-alert.wav");
      audio
        .play()
        .catch((error) =>
          console.warn("Order alert sound could not be played", error)
        );
      lastSoundAtRef.current = now;
    }

    const timer = window.setTimeout(() => {
      dispatch(clearBurst());
    }, 1200);

    return () => window.clearTimeout(timer);
  }, [burstCount, dispatch, isOnOrdersPage, muted]);

  const handleGoToOrders = () => {
    setOpen(false);
    dispatch(clearBurst());
    navigate("/app/order");
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={6500}
      onClose={() => setOpen(false)}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Alert
        severity="info"
        variant="filled"
        sx={{
          bgcolor: "#16100f",
          color: "#f0ece8",
          border: "1px solid rgba(131, 60, 65, 0.32)",
          boxShadow: "0 18px 40px rgba(0,0,0,0.28)",
          alignItems: "center",
        }}
        action={
          <Stack direction="row" spacing={0.5} alignItems="center">
            <IconButton
              size="small"
              onClick={() => dispatch(toggleOrderNotificationMute())}
              sx={{ color: "#f0ece8" }}
              title={muted ? "Unmute order sound" : "Mute order sound"}
            >
              {muted ? (
                <NotificationsOffOutlinedIcon fontSize="small" />
              ) : (
                <NotificationsActiveOutlinedIcon fontSize="small" />
              )}
            </IconButton>
            <Button
              size="small"
              onClick={handleGoToOrders}
              sx={{ color: "#d5a84c", fontWeight: 800 }}
            >
              View
            </Button>
          </Stack>
        }
      >
        <Typography sx={{ fontWeight: 900, fontSize: 14 }}>
          {toastCount > 1
            ? `${toastCount} new orders received`
            : "New order received"}
        </Typography>
        <Typography sx={{ fontSize: 12, color: "#c8b8b0" }}>
          {unseenOrderIds.length} unseen order
          {unseenOrderIds.length === 1 ? "" : "s"}
          {unseenSessionIds.length
            ? ` across ${unseenSessionIds.length} table session${
                unseenSessionIds.length === 1 ? "" : "s"
              }`
            : ""}
        </Typography>
      </Alert>
    </Snackbar>
  );
}
